import { useState, useMemo, useEffect } from "react";
import { useHistory } from "react-router-dom";
import {
  Flex,
  Button,
  ModalV2,
  FileUpload,
  FileObjectType,
  toast,
  Banner,
  Text,
} from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { Colors } from "@jsq/ds/styles";
import { globalEnv } from "~/global-env";
import { type S3DocumentInput } from "~/graphql/mainTypes";
import { useCreateDocumentsMutation } from "~/graphql/supergraphHooks";
import useComplianceUploadFilesToS3 from "~/hooks/uploadFilesToComplianceS3/useComplianceUploadFilesToS3";
import { useCurrentCaseContext } from "~/pages/Compliance/context/CurrentCaseContext";
import { useGuidedProfileCreation } from "~/pages/Compliance/flags";
import { getErrorMessage } from "~/utils/errors";
import { formatCountText } from "~/utils/strings";
import { ProfileCreationModalFooter } from "../../Profiles/common/AutoProfileCreationCard/ProfileCreationModal/ProfileCreationModalFooter";
import {
  ExtractionStatus,
  useDocumentExtractionPolling,
} from "../../Profiles/common/AutoProfileCreationCard/ProfileCreationModal/ProfileCreationModalFooter/useDocumentExtractionPolling";
import {
  errorBanner,
  inlineErrorBanner,
} from "../../Profiles/common/AutoProfileCreationCard/ProfileCreationModal/styles_vanilla.css";
import { CreatedDocuments } from "./types";
import {
  EXTRACTION_FAILED_ERROR,
  FILE_TYPE_NOT_SUPPORTED_ERROR,
  UNSUPPORTED_DOC_TYPE_ERROR,
  getBannerTitle,
  getFileCardError,
} from "./utils";

interface Props {
  showModal: boolean;
  onClose: () => void;
  caseId: number;
}

const UploadModal = ({ showModal, onClose, caseId }: Props) => {
  const history = useHistory();
  const { aiExtractionSupported, isIndividualRootProfile } = useCurrentCaseContext();
  const guidedProfileCreation = useGuidedProfileCreation();
  const isAiExtractionEnabled =
    aiExtractionSupported && !(guidedProfileCreation && isIndividualRootProfile);
  const [buttonLoading, setIsButtonLoading] = useState(false);
  const [uploadMode, setUploadMode] = useState<"uploadWithAI" | "uploadOnly" | null>(null);
  const { uploadFilesToS3 } = useComplianceUploadFilesToS3();
  const [createDocuments] = useCreateDocumentsMutation({
    refetchQueries: ["GetComplianceCase"],
    awaitRefetchQueries: true,
  });
  const [files, setFiles] = useState<FileObjectType[]>([]);
  const [firstUploadedDocumentId, setFirstUploadedDocumentId] = useState<number>();
  const [errorMessage, setErrorMessage] = useState("");

  const { extractionStatus } = useDocumentExtractionPolling({
    documentId: firstUploadedDocumentId,
  });

  const isEscalationRoute = useMemo(() => {
    return location.pathname.startsWith(`${globalEnv.complianceRoute}/escalations`);
  }, [location.pathname]);

  const handleClose = () => {
    setFiles([]);
    setIsButtonLoading(false);
    setErrorMessage("");
    setUploadMode(null);
    onClose();
  };

  const handleManualCreate = () => {
    handleClose();
  };

  const serverRejectedFiles = useMemo(() => {
    const fileCardError = getFileCardError(errorMessage);
    if (!fileCardError || files.length === 0) return undefined;
    return files.map((f) => ({ file: f.file, error: fileCardError }));
  }, [errorMessage, files]);

  const handleFilesSelected = (selectedFiles: FileObjectType[]) => {
    const acceptedFiles = selectedFiles.filter((file) => file.status === "accepted");
    const hasRejectedFiles = selectedFiles.some((file) => file.status === "rejected");
    setFiles(acceptedFiles);
    setErrorMessage(hasRejectedFiles ? FILE_TYPE_NOT_SUPPORTED_ERROR : "");
  };

  const uploadDocuments = async (
    files: FileObjectType[],
    processExtraction: boolean,
  ): Promise<CreatedDocuments> => {
    const uploadResponse = await uploadFilesToS3(files.map((x) => x.file));
    const s3ObjectKeys = uploadResponse.map((s3File): S3DocumentInput => {
      return {
        objectKey: s3File.objectKey,
        name: s3File.file.name,
        size: s3File.file.size,
        type: s3File.file.type,
      };
    });

    const documentInputs = s3ObjectKeys.map((s3ObjectKey) => ({
      contentType: s3ObjectKey.type,
      fileName: s3ObjectKey.name || "",
      s3Key: s3ObjectKey.objectKey || "",
    }));

    const createdDocumentsResult = await createDocuments({
      variables: {
        caseId: caseId,
        documents: documentInputs,
        processExtraction: processExtraction,
      },
    });

    if (createdDocumentsResult.errors) {
      throw new Error(createdDocumentsResult.errors[0].message);
    }

    const createdDocuments = createdDocumentsResult.data
      ? createdDocumentsResult.data.createComplianceDocuments
      : [];
    const numberOfDocuments = createdDocuments.length;
    toast.success(`${formatCountText(numberOfDocuments, "file")} uploaded successfully`);

    return createdDocuments;
  };

  const navigateOnSuccess = (firstDocumentId?: number) => {
    const baseRoute = isEscalationRoute ? "escalations" : "case";
    const path = firstDocumentId
      ? `${globalEnv.complianceRoute}/${baseRoute}/${caseId}/document-management?documentId=${firstDocumentId}`
      : `${globalEnv.complianceRoute}/${baseRoute}/${caseId}/document-management`;
    history.push(path);
  };

  const handleUploadWithAI = async () => {
    if (files.length === 0) return;

    setIsButtonLoading(true);
    setUploadMode("uploadWithAI");

    for (const file of files) {
      const type = file.file.type.toLowerCase();
      if (type !== "application/pdf" && !type.startsWith("image/")) {
        setErrorMessage(FILE_TYPE_NOT_SUPPORTED_ERROR);
        setIsButtonLoading(false);
        return;
      }
    }

    try {
      const documents = await uploadDocuments(files, true);
      const defaultDocumentId = documents[0].id;
      setFirstUploadedDocumentId(defaultDocumentId);
    } catch (error) {
      toast.error(getErrorMessage(error));
      return;
    } finally {
      setIsButtonLoading(false);
    }
  };

  const handleUploadOnly = async () => {
    if (files.length === 0) return;

    setIsButtonLoading(true);
    setUploadMode("uploadOnly");

    try {
      const documents = await uploadDocuments(files, false);
      const defaultDocumentId = documents[0].id;
      handleClose();
      navigateOnSuccess(defaultDocumentId);
    } catch (e) {
      toast.error(getErrorMessage(e));
      return;
    } finally {
      setIsButtonLoading(false);
    }
  };

  useEffect(() => {
    if (!firstUploadedDocumentId) return;

    if (extractionStatus === ExtractionStatus.Succeeded) {
      handleClose();
      navigateOnSuccess(firstUploadedDocumentId);
    } else if (extractionStatus === ExtractionStatus.UnsupportedDocType) {
      setErrorMessage(UNSUPPORTED_DOC_TYPE_ERROR);
      setIsButtonLoading(false);
    } else if (extractionStatus === ExtractionStatus.Failed) {
      setErrorMessage(EXTRACTION_FAILED_ERROR);
      setIsButtonLoading(false);
    }
  }, [extractionStatus, firstUploadedDocumentId]);

  return (
    <ModalV2 show={showModal} onHide={handleClose} title="Select files">
      <ModalV2.Body>
        {errorMessage &&
          (errorMessage === EXTRACTION_FAILED_ERROR ? (
            <div className={inlineErrorBanner}>
              <Icon type="exclamation-circle" color={Colors.Danger600} variant="solid" />
              <Text asSpan>
                Please upload a different document or{" "}
                <Button displayAsLink isInline onClick={handleManualCreate}>
                  manually create a profile
                </Button>
                .
              </Text>
            </div>
          ) : (
            <Banner
              variant="danger"
              title={getBannerTitle(errorMessage)}
              className={errorBanner}
            />
          ))}
        <FileUpload
          isFullWidth
          canAllowMultiple
          onFileSelected={handleFilesSelected}
          acceptedFileTypes={[
            "image/*",
            ".pdf",
            ".doc",
            ".docx",
            ".xlsx",
            ".xls",
            ".csv",
            ".zip",
          ]}
          serverRejectedFiles={serverRejectedFiles}
        />

        {isAiExtractionEnabled && files.length > 0 && (
          <Flex flexDirection="column" alignItems="center" gap={20}>
            {uploadMode !== "uploadOnly" && (
              <ProfileCreationModalFooter
                isUploading={buttonLoading}
                onUploadWithAI={handleUploadWithAI}
                onDocumentUploadSuccess={firstUploadedDocumentId !== undefined}
                extractionStatus={extractionStatus}
              />
            )}
            {uploadMode !== "uploadWithAI" && (
              <Button displayAsLink={true} onClick={handleUploadOnly} isLoading={buttonLoading}>
                Or just upload documents only
              </Button>
            )}
          </Flex>
        )}
      </ModalV2.Body>
      {!isAiExtractionEnabled && (
        <ModalV2.Footer>
          <Flex flexDirection="row" flexWrap="nowrap" justifyContent="flex-end">
            <Button
              className="inline-sm"
              variant="neutral"
              type="button"
              data-cy="cancel-button"
              onClick={handleClose}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              isLoading={buttonLoading}
              onClick={handleUploadOnly}
              data-cy="submit-button"
            >
              Upload
            </Button>
          </Flex>
        </ModalV2.Footer>
      )}
    </ModalV2>
  );
};

export default UploadModal;
